import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, X, Pencil, UserCheck, UserX, KeyRound, Copy, Check } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import type { User, Role, UserStatus } from '@billflow/shared';

// ─── Badge helpers ─────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: Role }) {
  return role === 'admin' ? (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
      Admin
    </span>
  ) : (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
      Operator
    </span>
  );
}

function StatusBadge({ status }: { status: UserStatus }) {
  return status === 'active' ? (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
      Inactive
    </span>
  );
}

// ─── Modal ─────────────────────────────────────────────────────────────────────

interface ModalProps {
  mode: 'create' | 'edit';
  user?: User;
  onClose: () => void;
  onSaved: () => void;
}

function UserModal({ mode, user, onClose, onSaved }: ModalProps) {
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'operator');
  const [status, setStatus] = useState<UserStatus>(user?.status ?? 'active');
  const [password, setPassword] = useState('');
  const [forcePasswordChange, setForcePasswordChange] = useState(
    user?.force_password_change ?? false,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'create') {
        if (password.length < 8) {
          setError('Password must be at least 8 characters.');
          return;
        }
        await apiFetch('/api/users', {
          method: 'POST',
          body: JSON.stringify({
            username,
            full_name: fullName,
            email: email.trim() || undefined,
            password,
            role,
          }),
        });
        toast.success('User created');
      } else {
        const body: Record<string, unknown> = {
          full_name: fullName,
          email: email.trim(), // '' clears the email server-side
          role,
          status,
          force_password_change: forcePasswordChange,
        };
        if (password) body.password = password;
        await apiFetch(`/api/users/${user!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('User updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">
            {mode === 'create' ? 'Create User' : 'Edit User'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === 'create' && (
            <div className="space-y-1.5">
              <Label htmlFor="modal-username">Username</Label>
              <Input
                id="modal-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                autoComplete="off"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="modal-fullname">Full Name</Label>
            <Input
              id="modal-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="modal-email">
              Email <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="modal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="off"
              placeholder="name@example.com"
            />
            <p className="text-xs text-muted-foreground">Used for password reset links.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="modal-role">Role</Label>
            <select
              id="modal-role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={loading}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {mode === 'edit' && (
            <div className="space-y-1.5">
              <Label htmlFor="modal-status">Status</Label>
              <select
                id="modal-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as UserStatus)}
                disabled={loading}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="modal-password">
              {mode === 'create' ? 'Password' : 'New Password'}
              {mode === 'edit' && (
                <span className="ml-1 font-normal text-muted-foreground">
                  (leave blank to keep)
                </span>
              )}
            </Label>
            <Input
              id="modal-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={mode === 'create'}
              disabled={loading}
              autoComplete="new-password"
              placeholder={mode === 'edit' ? '••••••••' : ''}
            />
          </div>

          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <input
                id="modal-force-pw"
                type="checkbox"
                checked={forcePasswordChange}
                onChange={(e) => setForcePasswordChange(e.target.checked)}
                disabled={loading}
                className="h-4 w-4 rounded border-gray-300 accent-primary"
              />
              <Label htmlFor="modal-force-pw" className="cursor-pointer font-normal">
                Require password change on next login
              </Label>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : mode === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reset-password dialog ───────────────────────────────────────────────────────

function ResetPasswordDialog({
  user,
  tempPassword,
  onClose,
}: {
  user: User;
  tempPassword: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — copy it manually.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Temporary Password</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            A temporary password for{' '}
            <span className="font-medium text-foreground">{user.full_name}</span> has been
            generated. Share it with them securely.
          </p>

          <div className="flex items-center gap-2">
            <code className="flex-1 select-all rounded-md border bg-muted px-3 py-2 font-mono text-sm">
              {tempPassword}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={copy} className="shrink-0">
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            They&apos;ll be asked to change it at next login.
          </p>

          <div className="flex justify-end pt-2">
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function Users() {
  const { user: currentUser } = useAuthContext();
  const queryClient = useQueryClient();

  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [resetResult, setResetResult] = useState<{ user: User; tempPassword: string } | null>(null);
  const [resettingId, setResettingId] = useState<number | null>(null);

  const {
    rows: users,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
    isError,
    error,
  } = usePaginatedList<User>({
    endpoint: '/api/users',
    entityKey: 'users',
    defaultSort: 'created',
    defaultDir: 'desc',
  });

  function refetchUsers() {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }

  function openCreate() {
    setEditingUser(undefined);
    setModalMode('create');
  }

  function openEdit(u: User) {
    setEditingUser(u);
    setModalMode('edit');
  }

  async function resetPassword(u: User) {
    setResettingId(u.id);
    try {
      const res = await apiFetch<{ data: { tempPassword: string } }>(
        `/api/users/${u.id}/reset-password`,
        { method: 'POST' },
      );
      setResetResult({ user: u, tempPassword: res.data.tempPassword });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResettingId(null);
    }
  }

  async function toggleStatus(u: User) {
    const nextStatus: UserStatus = u.status === 'active' ? 'inactive' : 'active';
    try {
      await apiFetch(`/api/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      toast.success(nextStatus === 'active' ? 'User activated' : 'User deactivated');
      refetchUsers();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update user');
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Users</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage staff accounts and permissions
            </p>
          </div>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-1.5" />
          Create User
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or username…"
            className="pl-9"
          />
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-4">
          Failed to load users: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Full Name"
                sortKey="name"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
              />
              <SortableTh
                label="Username"
                sortKey="username"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
              <SortableTh label="Role" sortKey="role" sort={sort} dir={dir} onToggle={toggleSort} />
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
              />
              <SortableTh
                label="Created"
                sortKey="created"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Loading users…
                </td>
              </tr>
            )}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No users found.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const isSelf = currentUser?.id === u.id;
              return (
                <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    {u.full_name}
                    {isSelf && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {u.username}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString('en-CA')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(u)}
                        className="h-7 px-2 text-xs"
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resetPassword(u)}
                        disabled={resettingId === u.id}
                        className="h-7 px-2 text-xs"
                      >
                        <KeyRound className="h-3 w-3 mr-1" />
                        {resettingId === u.id ? 'Resetting…' : 'Reset password'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleStatus(u)}
                        disabled={isSelf}
                        className="h-7 px-2 text-xs"
                        title={isSelf ? 'You cannot deactivate your own account' : undefined}
                      >
                        {u.status === 'active' ? (
                          <>
                            <UserX className="h-3 w-3 mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <UserCheck className="h-3 w-3 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modal */}
      {modalMode && (
        <UserModal
          mode={modalMode}
          user={editingUser}
          onClose={() => setModalMode(null)}
          onSaved={refetchUsers}
        />
      )}

      {/* Temporary-password dialog */}
      {resetResult && (
        <ResetPasswordDialog
          user={resetResult.user}
          tempPassword={resetResult.tempPassword}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}
